import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Lock } from 'lucide-react';
import Navbar from '../components/Navbar';
import InventarioPinModal from '../components/cantina/productos/InventarioPinModal';
import useCajaCierre from '../hooks/caja/useCajaCierre';

const dinero = (valor) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(valor || 0));

const fechaCorta = (fecha) => {
  if (!fecha) return '';
  const [anio, mes, dia] = String(fecha).slice(0, 10).split('-');
  return `${dia}/${mes}/${anio}`;
};

const fechaHora = (valor) =>
  valor
    ? new Date(valor).toLocaleString('es-AR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '';

// El efectivo se cuenta a mano. En el resto de los medios se escribe el
// total del postnet o del banco, o se trae el total del sistema.
const esEfectivo = (nombre) =>
  String(nombre || '').trim().toLowerCase() === 'efectivo';

const aCentavos = (valor) => Math.round(Number(valor || 0) * 100);

const claseDiferencia = (diferencia) => {
  if (diferencia === null || diferencia === undefined) {
    return 'text-blanco-puro/40';
  }
  if (Math.abs(diferencia) < 0.005) return 'text-emerald-400';
  return diferencia < 0 ? 'text-red-400' : 'text-amber-300';
};

const textoDiferencia = (diferencia) => {
  if (diferencia === null || diferencia === undefined) return '—';
  if (Math.abs(diferencia) < 0.005) return 'Sin diferencia';
  return `${diferencia > 0 ? '+' : '-'}${dinero(Math.abs(diferencia))}`;
};

export default function CajaCierre() {
  const {
    fecha,
    setFecha,
    dia,
    cierres,
    cargando,
    error,
    cerrarDia,
  } = useCajaCierre();

  const [contados, setContados] = useState({});
  const [observaciones, setObservaciones] = useState('');
  const [pinAbierto, setPinAbierto] = useState(false);
  const [procesando, setProcesando] = useState(false);

  // Al cambiar de día se limpia lo que se había cargado.
  useEffect(() => {
    setContados({});
    setObservaciones('');
  }, [fecha]);

  const cerrado = Boolean(dia?.cerrado);
  const origen = cerrado ? dia.cierre : dia;
  const medios = origen?.medios || [];
  const totales = origen?.totales || {};

  const filas = useMemo(
    () =>
      medios.map((medio) => {
        const contado = cerrado
          ? medio.contado
          : contados[medio.medio_pago_id];
        const hayConteo =
          contado !== undefined && contado !== null && contado !== '';
        const diferencia = hayConteo
          ? (aCentavos(contado) - aCentavos(medio.esperado)) / 100
          : null;

        return { ...medio, contado, hayConteo, diferencia };
      }),
    [medios, contados, cerrado]
  );

  const resumenArqueo = useMemo(() => {
    const contadas = filas.filter((fila) => fila.hayConteo);

    if (!contadas.length) return null;

    return {
      contado:
        contadas.reduce((t, f) => t + aCentavos(f.contado), 0) / 100,
      diferencia:
        contadas.reduce((t, f) => t + (f.diferencia || 0) * 100, 0) / 100,
    };
  }, [filas]);

  const confirmarCierre = async (_usuario, pin) => {
    setProcesando(true);

    try {
      await cerrarDia({
        autorizacionPin: pin,
        observaciones: observaciones.trim(),
        conteos: filas
          .filter((fila) => fila.hayConteo)
          .map((fila) => ({
            medio_pago_id: fila.medio_pago_id,
            contado: Number(fila.contado),
          })),
      });

      setPinAbierto(false);
    } finally {
      setProcesando(false);
    }
  };

  const sinMovimiento =
    !cerrado && !filas.some((f) => f.ventas_cantina || f.pagos_restaurante);

  return (
    <>
      <Navbar />

      <main className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-black px-4 pb-12 pt-24 text-blanco-puro">
        <div className="mx-auto max-w-6xl">
          <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-amarillo-dorado">
                Caja
              </p>
              <h1 className="mt-1 text-3xl font-bold">Cierre de caja</h1>
              <p className="mt-2 text-sm text-blanco-puro/65">
                Cantina y Restaurante del día, por medio de pago. Se cierra
                un día por vez y lo hace un administrador.
              </p>
            </div>

            <label className="rounded-xl border border-white/10 bg-slate-800/80 p-4 text-xs text-blanco-puro/70">
              Día a cerrar
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="mt-1 block rounded-lg border border-white/20 bg-slate-950 px-3 py-2 text-sm text-blanco-puro"
              />
            </label>
          </div>

          {error && (
            <div className="mb-5 rounded-xl border border-red-400/40 bg-red-500/10 p-4 text-sm text-red-200">
              {error}
            </div>
          )}

          {cargando && !dia && (
            <p className="text-sm text-blanco-puro/60">Calculando…</p>
          )}

          {dia && (
            <>
              {cerrado ? (
                <div className="mb-5 flex items-start gap-3 rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-4 text-sm text-emerald-100">
                  <CheckCircle2 size={20} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">
                      Caja del {fechaCorta(dia.fecha)} cerrada
                    </p>
                    <p className="text-emerald-100/80">
                      Por {dia.cierre.cerrado_por?.nombre || 'un administrador'}{' '}
                      el {fechaHora(dia.cierre.cerrado_at)}. Las ventas de
                      Cantina de ese día ya no se pueden anular.
                    </p>
                    {dia.cierre.observaciones && (
                      <p className="mt-1">
                        Observaciones: {dia.cierre.observaciones}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                !dia.puede_cerrarse && (
                  <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-400/30 bg-amber-500/10 p-4 text-sm text-amber-100">
                    <Lock size={20} className="mt-0.5 shrink-0" />
                    <p>
                      Este día todavía no terminó. Se puede cerrar a partir
                      de mañana; mientras tanto ves lo acumulado.
                    </p>
                  </div>
                )
              )}

              <section className="overflow-hidden rounded-xl border border-white/10 bg-slate-800/80">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-950/70 text-left text-xs uppercase tracking-wide text-blanco-puro/50">
                      <tr>
                        <th className="px-5 py-3">Medio de pago</th>
                        <th className="px-5 py-3 text-right">Cantina</th>
                        <th className="px-5 py-3 text-right">Restaurante</th>
                        <th className="px-5 py-3 text-right">Esperado</th>
                        <th className="px-5 py-3 text-right">Contado / Postnet</th>
                        <th className="px-5 py-3 text-right">Diferencia</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.map((fila) => (
                        <tr
                          key={fila.medio_pago_id}
                          className="border-t border-blanco-puro/10"
                        >
                          <td className="px-5 py-3 font-medium">
                            {fila.nombre}
                          </td>
                          <td className="px-5 py-3 text-right">
                            {dinero(fila.monto_cantina)}
                            <span className="block text-xs text-blanco-puro/45">
                              {fila.ventas_cantina} ventas
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right">
                            {dinero(fila.monto_restaurante)}
                            <span className="block text-xs text-blanco-puro/45">
                              {fila.pagos_restaurante} pagos
                            </span>
                          </td>
                          <td className="px-5 py-3 text-right font-semibold">
                            {dinero(fila.esperado)}
                          </td>
                          <td className="px-5 py-3 text-right">
                            {cerrado ? (
                              fila.hayConteo ? (
                                dinero(fila.contado)
                              ) : (
                                <span className="text-blanco-puro/40">
                                  Sin contar
                                </span>
                              )
                            ) : (
                              <div className="flex flex-col items-end gap-1">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  inputMode="decimal"
                                  value={contados[fila.medio_pago_id] ?? ''}
                                  onChange={(e) =>
                                    setContados((actuales) => ({
                                      ...actuales,
                                      [fila.medio_pago_id]: e.target.value,
                                    }))
                                  }
                                  placeholder={
                                    esEfectivo(fila.nombre)
                                      ? 'Sin contar'
                                      : 'Total del postnet'
                                  }
                                  aria-label={`Monto contado en ${fila.nombre}`}
                                  className="w-40 rounded-lg border border-white/20 bg-slate-950 px-3 py-2 text-right text-sm text-blanco-puro"
                                />
                                {!esEfectivo(fila.nombre) && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setContados((actuales) => ({
                                        ...actuales,
                                        [fila.medio_pago_id]: String(
                                          fila.esperado
                                        ),
                                      }))
                                    }
                                    aria-label={`Traer el total de ${fila.nombre}`}
                                    className="text-xs font-semibold text-amarillo-dorado underline-offset-2 transition hover:underline"
                                  >
                                    Traer total del sistema
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                          <td
                            className={`px-5 py-3 text-right font-semibold ${claseDiferencia(
                              fila.diferencia
                            )}`}
                          >
                            {textoDiferencia(fila.diferencia)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-blanco-puro/20 bg-slate-950/50 font-semibold">
                      <tr>
                        <td className="px-5 py-3">Total</td>
                        <td className="px-5 py-3 text-right">
                          {dinero(totales.cantina)}
                        </td>
                        <td className="px-5 py-3 text-right">
                          {dinero(totales.restaurante)}
                        </td>
                        <td className="px-5 py-3 text-right">
                          {dinero(totales.esperado)}
                        </td>
                        <td className="px-5 py-3 text-right">
                          {resumenArqueo ? dinero(resumenArqueo.contado) : '—'}
                        </td>
                        <td
                          className={`px-5 py-3 text-right ${claseDiferencia(
                            resumenArqueo?.diferencia
                          )}`}
                        >
                          {textoDiferencia(resumenArqueo?.diferencia)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </section>

              <p className="mt-3 text-xs text-blanco-puro/50">
                El Restaurante incluye {dinero(totales.propinas)} de propinas
                cobradas ese día. La diferencia suma solo los medios en los
                que cargaste lo contado.
              </p>

              {!cerrado && (
                <section className="mt-6 rounded-xl border border-white/10 bg-slate-800/80 p-5">
                  <label className="block text-xs text-blanco-puro/70">
                    Observaciones (opcional)
                    <textarea
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      maxLength={500}
                      rows={2}
                      className="mt-1 block w-full rounded-lg border border-white/20 bg-slate-950 px-3 py-2 text-sm text-blanco-puro"
                    />
                  </label>

                  {sinMovimiento && dia.puede_cerrarse && (
                    <p className="mt-3 text-sm text-amber-200">
                      Ese día no tuvo ventas ni pagos registrados.
                    </p>
                  )}

                  <button
                    type="button"
                    disabled={!dia.puede_cerrarse || procesando}
                    onClick={() => setPinAbierto(true)}
                    className="mt-4 rounded-lg bg-amarillo-dorado px-5 py-2.5 text-sm font-bold text-slate-900 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Cerrar el {fechaCorta(fecha)}
                  </button>
                </section>
              )}
            </>
          )}

          <section className="mt-10">
            <h2 className="mb-3 font-semibold">Últimos cierres</h2>

            {cierres.length === 0 ? (
              <p className="text-sm text-blanco-puro/50">
                Todavía no se cerró ningún día.
              </p>
            ) : (
              <ul className="divide-y divide-blanco-puro/10 overflow-hidden rounded-xl border border-white/10 bg-slate-800/80 text-sm">
                {cierres.map((cierre) => (
                  <li key={cierre.id}>
                    <button
                      type="button"
                      onClick={() => setFecha(String(cierre.fecha).slice(0, 10))}
                      className="flex w-full flex-wrap items-center justify-between gap-2 px-5 py-3 text-left transition hover:bg-white/5"
                    >
                      <span className="font-medium">
                        {fechaCorta(cierre.fecha)}
                      </span>
                      <span className="text-blanco-puro/70">
                        {dinero(cierre.totales.esperado)}
                      </span>
                      <span
                        className={claseDiferencia(cierre.totales.diferencia)}
                      >
                        {textoDiferencia(cierre.totales.diferencia)}
                      </span>
                      <span className="text-xs text-blanco-puro/45">
                        {cierre.cerrado_por?.nombre}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>

      <InventarioPinModal
        abierto={pinAbierto}
        titulo={`Cerrar caja · ${fechaCorta(fecha)}`}
        descripcion="Solo un administrador puede cerrar la caja."
        etiquetaPin="PIN de administrador"
        procesando={procesando}
        onClose={() => {
          if (!procesando) setPinAbierto(false);
        }}
        onConfirm={confirmarCierre}
      />
    </>
  );
}
